import {
  Alert,
  Button,
  Grid,
  Snackbar,
  SvgIcon,
  TextField,
  Typography,
} from '@mui/material';
import { FC, useEffect, useState } from 'react';
import InstancesList from './InstancesList';
import { Search } from '@mui/icons-material';
import CustomPagination from '../../../components/forms/Pagination';
import { Instance, client, Result, PaginatedList } from '../../../app/services/api/orderManagementClient';
import { useTranslation } from 'react-i18next';
import useLogError from '../../../hooks/useLogError';
import { Section } from '../../../components/ui/Section';

type Props = {
  onCreateNew: () => void;
}

const InstancesTab: FC<Props> = ({ onCreateNew }) => {
  const { t } = useTranslation('instances');
  const { logError } = useLogError();

  const [instances, setInstances] = useState<Instance[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [page, setPage] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const [showSubmitStatusMessage, setShowSubmitStatusMessage] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmittedSuccessfully, setIsSubmittedSuccessfully] = useState<boolean>(true);

  const rowsPerPage = 10;

  const maxPages = () => {
    return Math.ceil(totalItems / rowsPerPage);
  };

  useEffect(() => {
    loadData(page, searchTerm);
  }, [page]);

  const onSearch = () => {
    setPage(0);
    loadData(0, searchTerm);
  };

  const loadData = (pageNumber: number, searchBy: string) => {
    setIsLoading(true);
    
    const options = {
      name: searchBy || undefined,
      pageNumber: pageNumber,
      pageSize: rowsPerPage,
    };

    client.getInstances(options)
      .then((result: Result<PaginatedList<Instance>>) => {
        if (result?.data) {
          setInstances(result.data.items || []);
          setTotalItems(result.data.totalCount ?? 0);
        }
      })
      .catch((error: Error) => {
        logError(error);
        setErrorMessage(t('loadErrorMessage'));
        setIsSubmittedSuccessfully(false);
        setShowSubmitStatusMessage(true);
      })
      .finally(() => setIsLoading(false));
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const onClear = () => {
    setPage(0);
    setSearchTerm('');
    loadData(0, '');
  };

  const handleCloseMessage = () => {
    setShowSubmitStatusMessage(false);
  };

  return (
    <>
      <Grid
        container
        spacing={5}>
        <Grid
          item
          lg={3}
          sm={6}
          xs={12}>
          <TextField
            label={t('search')}
            value={searchTerm}
            onChange={(event) => handleSearchChange(event.target.value)}
            fullWidth
          />
        </Grid>
        <Grid
          item
          lg={2}
          sm={3}
          xs={6}>
          <Button
            fullWidth
            onClick={onClear}
            variant='secondary'
            size='lg'
            sx={{ mr: 4 }}
          >
            <Typography>{t('clear')}</Typography>
          </Button>
        </Grid>
        <Grid
          item
          lg={2}
          sm={3}
          xs={6}>
          <Button
            fullWidth
            onClick={onSearch}
            size='lg'
            startIcon={<SvgIcon><Search /></SvgIcon>}
          >
            <Typography>{t('search')}</Typography>
          </Button>
        </Grid>
        <Grid
          item
          xs={12}>
          <Section>
            <InstancesList
              list={instances}
              totalCount={totalItems}
              isLoading={isLoading}
              onClear={onClear}
              onCreateNew={onCreateNew}
            />
            <CustomPagination
              page={page}
              setPage={setPage}
              maxPages={maxPages()}
              breakpointForChangeDisplay={120000}
              useNoDataMessage={false}
            />
          </Section>
        </Grid>
      </Grid>

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={showSubmitStatusMessage}
        onClose={handleCloseMessage}
        autoHideDuration={5000}
      >
        <Alert
          onClose={handleCloseMessage}
          severity={isSubmittedSuccessfully ? 'success' : 'error'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {isSubmittedSuccessfully ? t('successMessage') : errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default InstancesTab;
